/**
 * Furniture preset system — assigns functional furniture to buildings based on
 * agent role and scene theme.
 *
 * Furniture represents agent capabilities and activities:
 * - comms-station  → inter-agent messaging / delegation
 * - inbox-tray     → task queue / receiving work
 * - monitor-bank   → computation / data processing
 * - server-rack    → external API / tool access
 * - whiteboard     → planning / strategy
 * - bulletin-board → shared context / broadcasting info
 * - bookshelf      → knowledge base / reference material
 * - filing-cabinet → results storage / archival
 * - conveyor       → pipeline throughput / assembly line
 * - paper-stack    → output / deliverables
 * - judge-bench    → evaluation / decision-making
 * - podium         → presenting / arguing a position
 */

import type { FurniturePlacement } from "./SceneLayout.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Agent role within the topology. */
export type AgentRole = "entry" | "exit" | "worker" | "hub";

/** Scene theme — one per topology type. */
export type SceneTheme =
  | "corporate"   // Star
  | "factory"     // Pipeline
  | "park"        // Hierarchical
  | "coworking"   // Mesh
  | "datacenter"  // MapReduce
  | "forum";      // Debate

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** A furniture item with local offset within a building. */
interface FurnitureItem {
  type: FurniturePlacement["type"];
  localCol: number;
  localRow: number;
}

/**
 * Get furniture placement for a building based on role and theme.
 *
 * Returns FurniturePlacement[] with grid coordinates offset by the building's
 * origin (buildingCol, buildingRow).
 */
export function getFurnitureForBuilding(
  role: AgentRole,
  theme: SceneTheme,
  buildingCol: number,
  buildingRow: number,
  width: number,
  height: number,
): FurniturePlacement[] {
  const items = getPresetItems(role, theme, width, height);
  return items.map((item) => ({
    type: item.type,
    gridCol: buildingCol + item.localCol,
    gridRow: buildingRow + item.localRow,
  }));
}

// ---------------------------------------------------------------------------
// Preset dispatch
// ---------------------------------------------------------------------------

function getPresetItems(
  role: AgentRole,
  theme: SceneTheme,
  w: number,
  h: number,
): FurnitureItem[] {
  switch (role) {
    case "entry": return getEntryItems(theme, w, h);
    case "exit": return getExitItems(theme, w, h);
    case "hub": return getHubItems(theme, w, h);
    case "worker": return getWorkerItems(theme, w, h);
  }
}

// ---------------------------------------------------------------------------
// Entry: receives tasks, delegates work, monitors progress
// Core items: inbox-tray (receives), comms-station (delegates), monitor-bank (monitors)
// ---------------------------------------------------------------------------

function getEntryItems(theme: SceneTheme, w: number, h: number): FurnitureItem[] {
  switch (theme) {
    case "corporate":
      // Manager's office: monitors team, delegates, stores reports
      return [
        { type: "monitor-bank", localCol: 1, localRow: 1 },           // monitors team progress
        { type: "comms-station", localCol: w - 2, localRow: 1 },      // delegates to workers
        { type: "filing-cabinet", localCol: w - 2, localRow: h - 2 }, // stores reports
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives tasks
      ];

    case "factory":
      // Floor supervisor: oversees pipeline, plans stages, tracks output
      return [
        { type: "monitor-bank", localCol: 1, localRow: 1 },           // oversees pipeline
        { type: "whiteboard", localCol: w - 2, localRow: 1 },         // plans pipeline stages
        { type: "conveyor", localCol: Math.floor(w / 2), localRow: h - 2 }, // feeds items into pipeline
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives input tasks
      ];

    case "park":
      // Team lead: plans, shares knowledge, distributes work
      return [
        { type: "bulletin-board", localCol: 1, localRow: 1 },         // broadcasts assignments
        { type: "bookshelf", localCol: w - 2, localRow: 1 },          // knowledge base
        { type: "comms-station", localCol: w - 2, localRow: h - 2 },  // delegates to children
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives tasks
      ];

    case "coworking":
      // Peer coordinator: plans, communicates, shares context
      return [
        { type: "whiteboard", localCol: 1, localRow: 1 },             // strategy planning
        { type: "bulletin-board", localCol: w - 2, localRow: 1 },     // shared context
        { type: "comms-station", localCol: w - 2, localRow: h - 2 },  // communicates with peers
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives tasks
      ];

    case "datacenter":
      // Map coordinator: distributes work, monitors workers, uses APIs
      return [
        { type: "server-rack", localCol: 1, localRow: 1 },            // external API access
        { type: "monitor-bank", localCol: Math.floor(w / 2), localRow: 1 }, // monitors worker fleet
        { type: "comms-station", localCol: w - 2, localRow: 1 },      // distributes tasks
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives input
      ];

    case "forum":
      // Judge: evaluates arguments, makes rulings, stores precedent
      return [
        { type: "judge-bench", localCol: Math.floor(w / 2), localRow: 1 }, // evaluation seat
        { type: "bookshelf", localCol: w - 2, localRow: h - 2 },      // legal precedent
        { type: "filing-cabinet", localCol: 1, localRow: h - 2 },     // case records
        { type: "paper-stack", localCol: 1, localRow: 1 },            // written rulings
      ];
  }
}

// ---------------------------------------------------------------------------
// Worker: processes tasks, produces output, uses tools
// Core items: monitor-bank (compute), paper-stack (output), server-rack (tools)
// ---------------------------------------------------------------------------

function getWorkerItems(theme: SceneTheme, w: number, h: number): FurnitureItem[] {
  switch (theme) {
    case "corporate":
      // Office worker: processes tasks, produces reports, checks references
      return [
        { type: "monitor-bank", localCol: 1, localRow: 1 },           // processes work
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // produces deliverables
        { type: "inbox-tray", localCol: w - 2, localRow: 1 },         // receives assignments
      ];

    case "factory":
      // Production worker: runs pipeline stage, processes + forwards output
      return [
        { type: "monitor-bank", localCol: 1, localRow: 1 },           // runs processing
        { type: "conveyor", localCol: Math.floor(w / 2), localRow: h - 2 }, // passes output downstream
        { type: "paper-stack", localCol: w - 2, localRow: 1 },        // stage output
      ];

    case "park":
      // Field worker: uses knowledge base, reports back, does research
      return [
        { type: "bookshelf", localCol: 1, localRow: 1 },              // reference material
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // research output
        { type: "comms-station", localCol: w - 2, localRow: 1 },      // reports to parent
      ];

    case "coworking":
      // Collaborator: brainstorms, shares ideas, uses tools
      return [
        { type: "whiteboard", localCol: 1, localRow: 1 },             // brainstorming / ideas
        { type: "comms-station", localCol: w - 2, localRow: 1 },      // communicates with peers
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // output
      ];

    case "datacenter":
      // Compute node: runs jobs, calls APIs, produces results
      return [
        { type: "server-rack", localCol: 1, localRow: 1 },            // runs computation
        { type: "monitor-bank", localCol: w - 2, localRow: 1 },       // job monitoring
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // receives work units
      ];

    case "forum":
      // Debater: prepares arguments, presents position
      if (w <= 3) {
        // Small booth — just a podium and notes
        return [
          { type: "podium", localCol: 1, localRow: 0 },               // presents arguments
          { type: "paper-stack", localCol: 0, localRow: h - 1 },      // written arguments
        ];
      }
      return [
        { type: "podium", localCol: Math.floor(w / 2), localRow: 1 }, // presents arguments
        { type: "bookshelf", localCol: 1, localRow: 1 },              // research / evidence
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // written arguments
      ];
  }
}

// ---------------------------------------------------------------------------
// Exit: collects results, produces final output, archives
// Core items: filing-cabinet (collects), inbox-tray (receives), paper-stack (final output)
// ---------------------------------------------------------------------------

function getExitItems(theme: SceneTheme, w: number, h: number): FurnitureItem[] {
  switch (theme) {
    case "corporate":
      return [
        { type: "filing-cabinet", localCol: w - 2, localRow: 1 },     // archives all results
        { type: "inbox-tray", localCol: 1, localRow: 1 },             // receives from workers
        { type: "monitor-bank", localCol: Math.floor(w / 2), localRow: h - 2 }, // reviews final output
        { type: "paper-stack", localCol: 1, localRow: h - 2 },        // final deliverable
      ];

    case "factory":
      return [
        { type: "filing-cabinet", localCol: w - 2, localRow: 1 },     // quality records
        { type: "conveyor", localCol: 1, localRow: 1 },               // receives pipeline output
        { type: "monitor-bank", localCol: Math.floor(w / 2), localRow: h - 2 }, // final inspection
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // shipping manifest
      ];

    case "park":
      return [
        { type: "filing-cabinet", localCol: w - 2, localRow: 1 },     // archives reports
        { type: "inbox-tray", localCol: 1, localRow: 1 },             // receives from field
        { type: "bulletin-board", localCol: Math.floor(w / 2), localRow: h - 2 }, // publishes findings
        { type: "paper-stack", localCol: 1, localRow: h - 2 },        // final report
      ];

    case "coworking":
      return [
        { type: "filing-cabinet", localCol: w - 2, localRow: 1 },     // organizes contributions
        { type: "inbox-tray", localCol: 1, localRow: 1 },             // collects from peers
        { type: "whiteboard", localCol: Math.floor(w / 2), localRow: h - 2 }, // synthesis
        { type: "paper-stack", localCol: w - 2, localRow: h - 2 },    // merged output
      ];

    case "datacenter":
      return [
        { type: "server-rack", localCol: 1, localRow: 1 },            // reduce computation
        { type: "inbox-tray", localCol: w - 2, localRow: 1 },         // receives mapped results
        { type: "monitor-bank", localCol: Math.floor(w / 2), localRow: h - 2 }, // aggregation dashboard
        { type: "filing-cabinet", localCol: w - 2, localRow: h - 2 }, // result storage
      ];

    case "forum":
      return [
        { type: "filing-cabinet", localCol: w - 2, localRow: 1 },     // case archive
        { type: "inbox-tray", localCol: 1, localRow: 1 },             // receives arguments
        { type: "podium", localCol: Math.floor(w / 2), localRow: h - 2 }, // verdict delivery
        { type: "paper-stack", localCol: 1, localRow: h - 2 },        // final ruling
      ];
  }
}

// ---------------------------------------------------------------------------
// Hub: central communicator, relays between many agents
// Core items: comms-station (central comms), monitor-bank (overview), bulletin-board (broadcasts)
// ---------------------------------------------------------------------------

function getHubItems(theme: SceneTheme, w: number, h: number): FurnitureItem[] {
  switch (theme) {
    case "corporate":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // central communications
        { type: "monitor-bank", localCol: w - 2, localRow: 1 },       // status overview
        { type: "bulletin-board", localCol: w - 2, localRow: h - 2 }, // broadcasts to team
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // message queue
      ];

    case "factory":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // coordination channel
        { type: "monitor-bank", localCol: w - 2, localRow: 1 },       // pipeline overview
        { type: "conveyor", localCol: Math.floor(w / 2), localRow: h - 2 }, // routes items
        { type: "whiteboard", localCol: 1, localRow: h - 2 },         // routing plan
      ];

    case "park":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // relay station
        { type: "bulletin-board", localCol: w - 2, localRow: 1 },     // broadcasts updates
        { type: "bookshelf", localCol: w - 2, localRow: h - 2 },      // shared knowledge
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // message relay
      ];

    case "coworking":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // peer-to-peer hub
        { type: "whiteboard", localCol: w - 2, localRow: 1 },         // shared planning
        { type: "bulletin-board", localCol: w - 2, localRow: h - 2 }, // community board
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // message queue
      ];

    case "datacenter":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // network hub
        { type: "server-rack", localCol: w - 2, localRow: 1 },        // routing infrastructure
        { type: "monitor-bank", localCol: w - 2, localRow: h - 2 },   // traffic overview
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // message buffer
      ];

    case "forum":
      return [
        { type: "comms-station", localCol: 1, localRow: 1 },          // moderator channel
        { type: "bulletin-board", localCol: w - 2, localRow: 1 },     // topic board
        { type: "bookshelf", localCol: w - 2, localRow: h - 2 },      // reference material
        { type: "inbox-tray", localCol: 1, localRow: h - 2 },         // submission queue
      ];
  }
}
